/** Canonical role compatibility contract shared by WebUI auth boundaries. */

export const ACCESS_CONTRACT_VERSION = '2026-10-14'

export const CANONICAL_ROLES = [
  'cml_admin',
  'cml_monitor',
  'cml_operations',
  'firm_owner',
  'firm_admin',
  'ca_partner',
  'technical_assistant',
  'research_assistant',
  'individual_client',
  'business_owner_client',
  'authorized_client',
  'independent_client',
] as const

export type CanonicalUserRole = (typeof CANONICAL_ROLES)[number]

export const PLATFORM_PERMISSIONS = [
  'txh.platform.legal_control.subject.inspect',
  'txh.platform.legal_control.subject.review',
  'txh.platform.legal_control.policy.submit',
  'txh.platform.legal_control.policy.decide',
  'txh.platform.legal_control.policy.retire',
  'txh.platform.tenant.read',
  'txh.platform.tenant.create',
  'txh.platform.tenant.update',
  'txh.platform.tenant.archive',
  'txh.platform.user.read',
  'txh.platform.user.create',
  'txh.platform.user.update',
  'txh.platform.user.deactivate',
  'txh.platform.user.role.assign',
  'txh.platform.user.session.revoke',
  'txh.platform.audit.read',
  'txh.platform.notification.review',
  'txh.platform.client.profile.review.submit',
  'txh.platform.client.profile.review.approve',
  'txh.platform.client.profile.verification.revoke',
  'txh.platform.payment.failed_refund.review',
  'txh.platform.document.historical_fence.review',
  'txh.monitoring.service.read',
  'txh.platform.deletion.read',
  'txh.platform.deletion.purge',
  'txh.platform.deletion.hold.manage',
  'txh.monitoring.health.execute',
  'txh.monitoring.operation.read',
  'txh.monitoring.restart.execute',
  'txh.support.session.read',
  'txh.support.session.create',
  'txh.support.session.approve',
  'txh.support.session.revoke',
  'txh.support.data.read',
  'txh.support.data.write',
] as const

export const PLATFORM_ROLE_PERMISSIONS = {
  cml_admin: PLATFORM_PERMISSIONS.filter(permission =>
    !permission.startsWith('txh.support.data.') &&
    permission !== 'txh.platform.legal_control.policy.submit' &&
    permission !== 'txh.platform.client.profile.review.submit'),
  cml_monitor: [
    'txh.platform.tenant.read',
    'txh.platform.user.read',
    'txh.platform.audit.read',
    'txh.monitoring.service.read',
    'txh.monitoring.health.execute',
    'txh.monitoring.operation.read',
    'txh.support.session.read',
    'txh.support.session.create',
    'txh.support.session.revoke',
    'txh.platform.deletion.read',
  ],
  cml_operations: [
    'txh.platform.legal_control.policy.submit',
    'txh.platform.notification.review',
    'txh.platform.client.profile.review.submit',
    'txh.platform.payment.failed_refund.review',
    'txh.platform.document.historical_fence.review',
    'txh.monitoring.service.read',
    'txh.monitoring.health.execute',
    'txh.monitoring.operation.read',
    'txh.monitoring.restart.execute',
  ],
} as const satisfies Record<'cml_admin' | 'cml_monitor' | 'cml_operations', readonly string[]>

export const ROLE_ALIASES = {
  owner: 'firm_owner',
  ca_owner: 'firm_owner',
  admin: 'firm_admin',
  partner: 'ca_partner',
  associate: 'ca_partner',
  ca_associate: 'ca_partner',
  senior_ca: 'ca_partner',
  assistant: 'technical_assistant',
  clerk: 'technical_assistant',
  ca_assistant: 'technical_assistant',
  ca_assistant_tech: 'technical_assistant',
  ca_assistant_research: 'research_assistant',
  client_individual: 'individual_client',
  client_business_owner: 'business_owner_client',
  client_authorized: 'authorized_client',
} as const satisfies Record<string, CanonicalUserRole>

export type ResolvedLegacyRole = keyof typeof ROLE_ALIASES

export const UNRESOLVED_COMPATIBILITY_ROLES = [
  'staff',
  'client',
  'client_user',
  'client_readonly',
  'client_admin',
] as const

export type UnresolvedCompatibilityRole = (typeof UNRESOLVED_COMPATIBILITY_ROLES)[number]
export type UserRole =
  | CanonicalUserRole
  | ResolvedLegacyRole
  | UnresolvedCompatibilityRole
  | 'unrecognized'

export interface RoleResolution {
  inputRole: string
  effectiveRole: UserRole
  canonicalRole: CanonicalUserRole | null
  status: 'canonical' | 'alias' | 'unresolved' | 'unknown'
  requiresClassification: boolean
}

const canonicalRoleSet = new Set<string>(CANONICAL_ROLES)
const unresolvedRoleSet = new Set<string>(UNRESOLVED_COMPATIBILITY_ROLES)

export function resolveRole(value: unknown): RoleResolution {
  const inputRole = typeof value === 'string' ? value.trim().toLowerCase() : ''
  if (canonicalRoleSet.has(inputRole)) {
    const canonicalRole = inputRole as CanonicalUserRole
    return {
      inputRole,
      effectiveRole: canonicalRole,
      canonicalRole,
      status: 'canonical',
      requiresClassification: false,
    }
  }
  if (inputRole in ROLE_ALIASES) {
    const canonicalRole = ROLE_ALIASES[inputRole as ResolvedLegacyRole]
    return {
      inputRole,
      effectiveRole: canonicalRole,
      canonicalRole,
      status: 'alias',
      requiresClassification: false,
    }
  }
  if (unresolvedRoleSet.has(inputRole)) {
    return {
      inputRole,
      effectiveRole: inputRole as UnresolvedCompatibilityRole,
      canonicalRole: null,
      status: 'unresolved',
      requiresClassification: true,
    }
  }
  return {
    inputRole,
    effectiveRole: 'unrecognized',
    canonicalRole: null,
    status: 'unknown',
    requiresClassification: false,
  }
}

export function normalizeRole(value: unknown): UserRole {
  return resolveRole(value).effectiveRole
}

export function resolveAssignableRole(value: unknown): CanonicalUserRole | null {
  const resolution = resolveRole(value)
  if (resolution.canonicalRole === 'independent_client') return null
  return resolution.requiresClassification ? null : resolution.canonicalRole
}

export const CANONICAL_ASSIGNABLE_ROLES: CanonicalUserRole[] = [
  'firm_admin',
  'ca_partner',
  'technical_assistant',
  'research_assistant',
  'individual_client',
  'business_owner_client',
  'authorized_client',
]

const ROLE_LABELS: Record<CanonicalUserRole, string> = {
  cml_admin: 'CML ADMIN',
  cml_monitor: 'CML MONITOR',
  cml_operations: 'CML OPERATIONS',
  firm_owner: 'CA Firm Owner',
  firm_admin: 'Firm Administrator',
  ca_partner: 'CA Partner',
  technical_assistant: 'Technical Assistant',
  research_assistant: 'Research Assistant',
  individual_client: 'Individual Client',
  business_owner_client: 'Business Owner Client',
  authorized_client: 'Authorized Client',
  independent_client: 'Independent account',
}

const ROLE_DESCRIPTIONS: Record<CanonicalUserRole, string> = {
  cml_admin: 'Tenantless platform administration and monitoring',
  cml_monitor: 'Read-only platform inventory and monitoring',
  cml_operations: 'Service monitoring and allowlisted operations',
  firm_owner: 'Firm ownership, billing, security, and unrestricted firm operations',
  firm_admin: 'Delegated organization, user, and security administration',
  ca_partner: 'Portfolio management, professional review, and approvals',
  technical_assistant: 'Assigned tax preparation, documents, computation, and filing support',
  research_assistant: 'Research and advisory support without filing or approval authority',
  individual_client: 'Self-service access to one personal taxpayer relationship',
  business_owner_client: 'Access to explicitly linked business entities',
  authorized_client: 'Time-bound and action-scoped representative access',
  independent_client:
    'Independent human authentication; client authority requires verified appointment',
}

export function getRoleLabel(role: UserRole): string {
  const resolution = resolveRole(role)
  if (resolution.canonicalRole) return ROLE_LABELS[resolution.canonicalRole]
  if (resolution.status === 'unresolved') {
    const label = resolution.inputRole.replace(/_/g, ' ')
    return `${label.replace(/\b\w/g, (char: string) => char.toUpperCase())} (Needs Classification)`
  }
  return 'Unrecognized Role'
}

export function getRoleDescriptionFromContract(role: UserRole): string {
  const resolution = resolveRole(role)
  if (resolution.canonicalRole) return ROLE_DESCRIPTIONS[resolution.canonicalRole]
  if (resolution.status === 'unresolved') return 'Legacy identity awaiting manual classification'
  return 'No access is granted for an unrecognized role'
}

export function roleCategory(role: UserRole): 'system' | 'ca_staff' | 'client' {
  const resolution = resolveRole(role)
  if (resolution.canonicalRole?.startsWith('cml_')) return 'system'
  if (
    resolution.canonicalRole === 'individual_client' ||
    resolution.canonicalRole === 'business_owner_client' ||
    resolution.canonicalRole === 'authorized_client' ||
    resolution.inputRole.startsWith('client')
  ) {
    return 'client'
  }
  return 'ca_staff'
}

const CANONICAL_ROLE_RANK: Record<CanonicalUserRole, number> = {
  cml_admin: 1000,
  cml_monitor: 900,
  cml_operations: 900,
  firm_owner: 100,
  firm_admin: 80,
  ca_partner: 60,
  technical_assistant: 40,
  research_assistant: 30,
  individual_client: 10,
  business_owner_client: 10,
  authorized_client: 5,
  independent_client: 0,
}

export const ROLE_HIERARCHY = new Proxy({} as Record<UserRole, number>, {
  get: (_target, key: string) => {
    const canonical = resolveRole(key).canonicalRole
    return canonical ? CANONICAL_ROLE_RANK[canonical] : 0
  },
})

export const ROLE_PERMISSION_SUMMARIES: Partial<Record<UserRole, string[]>> = {
  cml_admin: ['Platform administration', 'Monitoring', 'Support session governance'],
  cml_monitor: ['Read-only platform inventory', 'Monitoring', 'Audit'],
  cml_operations: ['Monitoring', 'Allowlisted operations'],
  firm_owner: ['Firm-wide operations', 'Billing ownership', 'Security administration'],
  firm_admin: ['User administration', 'Security settings', 'Firm configuration'],
  ca_partner: ['Portfolio management', 'Professional review', 'Approvals'],
  technical_assistant: ['Assigned tax preparation', 'Document handling', 'Filing support'],
  research_assistant: ['Research workspace', 'Advisory drafts', 'Review requests'],
  individual_client: ['Own taxpayer profile', 'Own documents', 'Own filing status'],
  business_owner_client: [
    'Linked business entities',
    'Business compliance',
    'Representative management',
  ],
  authorized_client: ['Granted records', 'Granted actions', 'Time-bound access'],
  independent_client: [
    'Account authentication only',
    'Client authority requires verified appointment',
  ],
  staff: ['No new assignments', 'Manual classification required'],
  client: ['No new assignments', 'Relationship classification required'],
  unrecognized: ['No access'],
}

export function getAssignableRolesFor(currentRole: UserRole): CanonicalUserRole[] {
  const canonical = resolveRole(currentRole).canonicalRole
  if (canonical === 'firm_owner') return [...CANONICAL_ASSIGNABLE_ROLES]
  if (canonical === 'firm_admin') {
    return CANONICAL_ASSIGNABLE_ROLES.filter(role => role !== 'firm_admin')
  }
  return []
}
